import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'

export type ProductValidationPendingNoticeProps = {
  /** Exibe o aviso (ex.: status === "PENDING") */
  open: boolean

  /** Indica se o produtor está com "Exigir validação prévia para novos produtos" ativo */
  requirePreValidation: boolean

  /** Status do produto (para ajustar o texto, se quiser) */
  productStatus?: 'PENDING' | 'DRAFT' | 'APPROVED' | 'REJECTED' | string

  /** Contatos (placeholders fáceis de trocar) */
  supportEmail?: string
  supportWhatsAppUrl?: string

  /** Ação para "ver detalhes da análise" (opcional) */
  onViewReviewDetails?: () => void

  /** Ação para "solicitar revisão / reenviar" (opcional) */
  onRequestReview?: () => void
}

/**
 * Aviso para o produtor no cadastro/edição de produto quando:
 * - Produto está pendente
 * - Conta do produtor exige validação prévia
 *
 * Uso:
 * <ProductValidationPendingNotice
 *   open={product.status === "PENDING"}
 *   requirePreValidation={producer.requirePreValidation}
 *   onViewReviewDetails={() => setOpenDrawer(true)}
 * />
 */
export function ProductValidationPendingNotice({
  open,
  requirePreValidation,
  productStatus = 'PENDING',
  supportEmail = 'suporte@celetus.com.br',
  supportWhatsAppUrl = 'https://wa.me/5500000000000',
  onViewReviewDetails,
  onRequestReview,
}: ProductValidationPendingNoticeProps) {
  // Se não há validação prévia exigida, normalmente não faz sentido exibir este aviso.
  const shouldShow = open && requirePreValidation && productStatus === 'PENDING'

  return (
    <Collapse in={shouldShow} unmountOnExit>
      <Box sx={{ mb: 2 }}>
        <Alert
          icon={<VerifiedUserOutlinedIcon fontSize="inherit" />}
          severity="info"
          sx={{
            borderRadius: 2,
            alignItems: 'flex-start',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography sx={{ fontWeight: 900 }}>
                Produto em validação
              </Typography>
              <Chip
                size="small"
                label="Pendente"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            </Stack>

            <Typography variant="body2">
              Este produto está com status <strong>Pendente</strong>. Antes de
              ficar disponível para venda, ele passará por uma análise de
              segurança e conformidade.
            </Typography>

            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoOutlinedIcon fontSize="small" sx={{ mt: 0.3 }} />
                <Typography variant="body2">
                  Enquanto estiver pendente, o produto{' '}
                  <strong>não aparecerá no checkout</strong> e não poderá
                  receber vendas.
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoOutlinedIcon fontSize="small" sx={{ mt: 0.3 }} />
                <Typography variant="body2">
                  Se você atualizar informações do produto (descrição, preço,
                  entrega, políticas), a análise pode reiniciar para garantir
                  consistência.
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ pt: 0.5 }}
            >
              {onViewReviewDetails && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onViewReviewDetails}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  Ver detalhes da análise
                </Button>
              )}

              {onRequestReview && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onRequestReview}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  Solicitar revisão
                </Button>
              )}

              <Button
                variant="text"
                size="small"
                startIcon={<SupportAgentOutlinedIcon />}
                sx={{
                  fontWeight: 800,
                  borderRadius: 2,
                  justifyContent: 'flex-start',
                }}
              >
                <Link
                  href={`mailto:${supportEmail}`}
                  underline="hover"
                  color="inherit"
                >
                  Falar com o suporte
                </Link>
              </Button>

              <Button
                variant="text"
                size="small"
                startIcon={<SupportAgentOutlinedIcon />}
                sx={{
                  fontWeight: 800,
                  borderRadius: 2,
                  justifyContent: 'flex-start',
                }}
              >
                <Link
                  href={supportWhatsAppUrl}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  color="inherit"
                >
                  Abrir WhatsApp
                </Link>
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Dica: mantenha a descrição do produto objetiva, informe
              prazos/forma de entrega e deixe claro o canal de suporte ao
              comprador. Isso acelera a validação.
            </Typography>
          </Stack>
        </Alert>
      </Box>
    </Collapse>
  )
}
